import React, {
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Icon,
  MessageModal,
  TextInput,
} from '@makeably/creativex-design-system';
import { addErrorToast } from 'components/organisms/Toasts';
import { track } from 'utilities/mixpanel';
import { post } from 'utilities/requests';
import { creativeCampaignsPath } from 'utilities/routes';
import styles from './NewCampaignModal.module.css';

const campaignProps = PropTypes.shape({
  name: PropTypes.string.isRequired,
  taxonomyName: PropTypes.string,
});

const propTypes = {
  campaigns: PropTypes.arrayOf(campaignProps).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

const instructions = 'Provide your creative campaign with a display name your team will recognize. '
  + 'If you already know the ad campaign name from your taxonomy, add it here to link your '
  + 'creative campaign automatically.';

function getUsedValues(campaigns) {
  if (campaigns) {
    return campaigns?.reduce((values, campaign) => {
      values.usedNames.push(campaign.name);
      if (campaign.taxonomyName) {
        values.usedTaxonomyNames.push(campaign.taxonomyName.toLowerCase());
      }

      return values;
    }, {
      usedNames: [],
      usedTaxonomyNames: [],
    });
  }

  return {
    usedNames: [],
    usedTaxonomyNames: [],
  };
}

function renderErrorMessage(message) {
  return (
    <div className="u-flexRow u-alignCenter t-body-2">
      <div className={styles.icon}>
        <Icon color="red" name="exclamationCircle" />
      </div>
      <div className={styles.message}>
        { message }
      </div>
    </div>
  );
}

async function createCampaign(name, taxonomyName) {
  const formData = new FormData();
  formData.append('name', name);
  formData.append('taxonomy_name', taxonomyName);

  const response = await post(creativeCampaignsPath(), formData);
  return response.data;
}

function NewCampaignModal({
  campaigns,
  isOpen,
  onClose,
  onSuccess,
}) {
  const [name, setName] = useState('');
  const [taxonomyName, setTaxonomyName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const { usedNames, usedTaxonomyNames } = useMemo(() => (
    getUsedValues(campaigns)
  ), [campaigns]);

  const nameTaken = usedNames.includes(name) || false;
  const taxonomyNameTaken = usedTaxonomyNames.includes(taxonomyName.toLowerCase()) || false;
  const invalidForm = !name || nameTaken || taxonomyNameTaken;

  const onCancel = () => {
    setName('');
    setTaxonomyName('');
    onClose();
  };

  const onSave = async () => {
    track('create_creative_campaign');
    setIsSaving(true);

    const response = await createCampaign(name, taxonomyName);

    setIsSaving(false);
    if (response?.id) {
      onSuccess({
        id: response.id,
        name: response.name,
        taxonomyName: response.taxonomyName,
      });
      onCancel();
    } else {
      addErrorToast(response?.error || 'Something went wrong, please try again');
    }
  };

  return (
    <MessageModal
      actionButtonDisabled={invalidForm || isSaving}
      actionButtonLabel="Create"
      isOpen={isOpen}
      title="New Creative Campaign"
      onActionButtonClick={onSave}
      onClose={onCancel}
    >
      <div className="u-flexColumn u-gap-16">
        <div className="t-body-1">
          { instructions }
        </div>
        <div className="u-flexColumn u-gap-8">
          <TextInput
            isError={nameTaken}
            label="Display Name"
            name="name"
            placeholder="Empty"
            size="large"
            value={name}
            onChange={setName}
          />
          { nameTaken && renderErrorMessage('Display Name Already Exists') }
        </div>
        <div className="u-flexColumn u-gap-8">
          <TextInput
            isError={taxonomyNameTaken}
            label="Parsed Name (optional)"
            name="taxonomy_name"
            placeholder="Optional"
            size="large"
            value={taxonomyName}
            onChange={setTaxonomyName}
          />
          { taxonomyNameTaken && renderErrorMessage('Parsed Name Already Exists') }
        </div>
      </div>
    </MessageModal>
  );
}

NewCampaignModal.propTypes = propTypes;

export default NewCampaignModal;
